export interface FilmChoice {
  id: string
  title: string
  order: string
}
export interface VoteStatus {
  isOpen: boolean
  submittedFilmId: string | null
}
export interface VoteReceipt {
  status: 'recorded' | 'already-recorded'
  filmId: string
}
export interface VoteResults {
  isOpen: boolean
  total: number
  films: Array<{ id: string; title: string; votes: number }>
  leaders: string[]
}
export interface VotingApi {
  status(token: string): Promise<VoteStatus>
  submit(token: string, filmId: string): Promise<VoteReceipt>
  results(): Promise<VoteResults>
  setOpen(isOpen: boolean): Promise<{ isOpen: boolean }>
}

export const browserBallotStorageKey = (awardId: string) => `${awardId}-browser-ballot`
export function getBrowserBallot(
  storage: Storage,
  cryptoProvider: Crypto,
  awardId = 'oddfest-2026'
): string {
  const STORAGE_KEY = browserBallotStorageKey(awardId)
  const previous = storage.getItem(STORAGE_KEY)
  if (previous && /^[a-f0-9]{64}$/.test(previous)) return previous
  const token = Array.from(cryptoProvider.getRandomValues(new Uint8Array(32)), (value) =>
    value.toString(16).padStart(2, '0')
  ).join('')
  storage.setItem(STORAGE_KEY, token)
  // Do not accept a vote without persistent storage: a refresh must reuse the
  // same receipt, including when the first response is lost on the network.
  if (storage.getItem(STORAGE_KEY) !== token) throw new Error('Browser storage unavailable.')
  return token
}

function element<T extends HTMLElement>(
  root: ParentNode,
  selector: string,
  type: { new (): T }
): T {
  const found = root.querySelector(selector)
  if (!(found instanceof type)) throw new Error(`Missing voting control: ${selector}`)
  return found
}

export function programChoices(document: Document): FilmChoice[] {
  return Array.from(document.querySelectorAll<HTMLElement>('article.film'))
    .filter((film) => film.dataset.awardEligible !== 'false')
    .map((film) => ({
      id: film.id,
      title: film.querySelector('h3')?.textContent ?? '',
      order: film.dataset.order ?? '',
    }))
}

export function mountAudienceVoting(
  root: HTMLElement,
  choices: FilmChoice[],
  api: VotingApi,
  token: string
) {
  const form = element(root, '#ballot-form', HTMLFormElement)
  const fieldset = element(root, '#ballot-choices', HTMLFieldSetElement)
  const status = element(root, '#vote-status', HTMLParagraphElement)
  const review = element(root, '#review-vote', HTMLButtonElement)
  const confirm = element(root, '#confirm-vote', HTMLButtonElement)
  const change = element(root, '#change-vote', HTMLButtonElement)
  const refresh = element(root, '#refresh-voting', HTMLButtonElement)
  const confirmation = element(root, '#vote-confirmation', HTMLElement)
  const selectedTitle = element(root, '#selected-film-title', HTMLElement)
  let votingOpen = false
  let submitted = false
  let chosen: FilmChoice | undefined
  let sending = false

  for (const choice of choices) {
    const label = document.createElement('label')
    label.className = 'ballot-choice'
    const radio = document.createElement('input')
    radio.type = 'radio'
    radio.name = 'favorite-film'
    radio.value = choice.id
    const text = document.createElement('span')
    text.textContent = `${choice.order} · ${choice.title}`
    label.append(radio, text)
    fieldset.append(label)
    radio.addEventListener('change', () => {
      chosen = choice
      review.disabled = !votingOpen || submitted
      confirmation.hidden = true
    })
  }
  const lock = () => {
    fieldset.disabled = sending || !votingOpen || submitted
    review.disabled = sending || !votingOpen || submitted || !chosen
    confirm.disabled = sending || submitted
    change.disabled = sending
  }
  const receipt = (filmId: string) => {
    const film = choices.find((choice) => choice.id === filmId)
    if (!film) throw new Error('Unknown ballot receipt.')
    submitted = true
    confirmation.hidden = true
    for (const radio of fieldset.querySelectorAll<HTMLInputElement>('input'))
      radio.checked = radio.value === filmId
    status.textContent = `Thank you. Your vote for ${film.title} is recorded.`
    lock()
  }
  async function refreshStatus() {
    refresh.disabled = true
    try {
      const result = await api.status(token)
      votingOpen = result.isOpen
      if (result.submittedFilmId) receipt(result.submittedFilmId)
      else {
        status.textContent = votingOpen
          ? 'Voting is open. Choose your favorite after watching the films.'
          : 'Voting is closed. Please check back when voting opens.'
        if (!votingOpen) confirmation.hidden = true
      }
    } catch {
      votingOpen = false
      confirmation.hidden = true
      status.textContent =
        'Voting could not be reached. Your program is still available. Try again below.'
    } finally {
      refresh.disabled = false
      lock()
    }
  }
  form.addEventListener('submit', (event) => {
    event.preventDefault()
    if (!chosen || submitted || !votingOpen) return
    selectedTitle.textContent = chosen.title
    confirmation.hidden = false
    confirm.focus()
  })
  change.addEventListener('click', () => {
    confirmation.hidden = true
    fieldset.querySelector<HTMLInputElement>('input:checked')?.focus()
  })
  confirm.addEventListener('click', async () => {
    if (!chosen || submitted || !votingOpen || sending) return
    sending = true
    lock()
    status.textContent = 'Submitting your vote…'
    try {
      receipt((await api.submit(token, chosen.id)).filmId)
    } catch {
      // Reconcile using the SAME token after an ambiguous network failure.
      // Never invent a success message or issue a new ballot token.
      try {
        const result = await api.status(token)
        votingOpen = result.isOpen
        if (result.submittedFilmId) receipt(result.submittedFilmId)
        else
          status.textContent = votingOpen
            ? 'Your vote was not confirmed. Please try again.'
            : 'Voting is closed. This vote was not recorded.'
      } catch {
        status.textContent = 'We could not confirm your vote. Keep this browser open and try again.'
      }
    } finally {
      sending = false
      lock()
    }
  })
  refresh.addEventListener('click', refreshStatus)
  root.hidden = false
  lock()
  return { ready: refreshStatus() }
}

export function mountOrganizerResults(root: HTMLElement, api: VotingApi) {
  const status = element(root, '#organizer-status', HTMLParagraphElement)
  const panel = element(root, '#private-results', HTMLElement)
  const body = element(root, '#results-body', HTMLTableSectionElement)
  const total = element(root, '#results-total', HTMLElement)
  const leaders = element(root, '#results-leaders', HTMLElement)
  const refresh = element(root, '#refresh-results', HTMLButtonElement)
  const toggle = element(root, '#toggle-voting', HTMLButtonElement)
  let isOpen = false
  let busy = false
  let generation = 0
  function clear() {
    generation += 1
    panel.hidden = true
    body.replaceChildren()
    total.textContent = ''
    leaders.textContent = ''
  }
  async function load() {
    if (busy) return
    busy = true
    refresh.disabled = true
    toggle.disabled = true
    // Remove old counts before revalidation (e.g. after role revocation).
    clear()
    const requestedGeneration = generation
    try {
      const results = await api.results()
      // Signing out while this request is in flight must not repaint totals.
      if (requestedGeneration !== generation) return
      isOpen = results.isOpen
      total.textContent = `${results.total} ${results.total === 1 ? 'ballot' : 'ballots'} recorded`
      for (const film of [...results.films].sort((a, b) => b.votes - a.votes)) {
        const row = document.createElement('tr')
        const title = document.createElement('td')
        title.textContent = film.title
        const count = document.createElement('td')
        count.textContent = String(film.votes)
        row.append(title, count)
        body.append(row)
      }
      const names = results.films
        .filter((film) => results.leaders.includes(film.id))
        .map((film) => film.title)
      leaders.textContent =
        names.length === 0
          ? 'No votes yet.'
          : `${names.length > 1 ? 'Tied for the lead' : 'Current leader'}: ${names.join(', ')}`
      status.textContent = isOpen
        ? 'Voting is open. These results are private.'
        : 'Voting is closed. These results are private.'
      toggle.textContent = isOpen ? 'Close voting' : 'Open voting'
      panel.hidden = false
    } catch {
      if (requestedGeneration === generation) {
        clear()
        status.textContent =
          'Results require a signed-in administrator. Sign in or refresh to try again.'
      }
    } finally {
      busy = false
      refresh.disabled = false
      toggle.disabled = false
    }
  }
  refresh.addEventListener('click', load)
  toggle.addEventListener('click', async () => {
    if (
      busy ||
      !window.confirm(
        isOpen
          ? 'Close audience voting now? Existing ballots will be kept.'
          : 'Open audience voting now? Existing ballots will be kept.'
      )
    )
      return
    busy = true
    toggle.disabled = true
    refresh.disabled = true
    try {
      await api.setOpen(!isOpen)
    } catch {
      status.textContent =
        'Voting availability could not be changed. Refresh to check the current state.'
    } finally {
      busy = false
      await load()
    }
  })
  return { refresh: load, clear }
}
